<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import MotorReservaRoomsAPI from 'dashboard/api/motorReservaRooms';

const { t } = useI18n();

const rooms = ref([]);
const isLoading = ref(false);
const expandedRoomId = ref(null);
const searchQuery = ref('');

// Form para nova unidade
const newUnitName = ref('');
const newUnitNumber = ref('');
const newUnitType = ref('Unidade Habitacional');
const isSubmitting = ref(false);

// Edição inline
const editingUnitId = ref(null);
const editUnitName = ref('');
const editUnitNumber = ref('');
const editUnitType = ref('');

const roomTypeOptions = [
  'Apartamento',
  'Bar',
  'Cabana',
  'Camping',
  'Casas',
  'Chalés',
  'Compartilhados',
  'Day Use',
  'Espaços',
  'Flats',
  'Hostel',
  'Hotéis',
  'Loft',
  'Pousadas',
  'Quarto',
  'Restaurantes',
  'Sala de Eventos',
  'Salas',
  'Sítios',
  'Suíte',
  'Terreno',
  'Unidade Habitacional',
];

const ic =
  'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none';

// Quartos pais (sem parent_id)
const parentRooms = computed(() => rooms.value.filter(r => !r.parent_id));

const filteredRooms = computed(() => {
  if (!searchQuery.value.trim()) return parentRooms.value;
  const q = searchQuery.value.toLowerCase();
  return parentRooms.value.filter(r => {
    const matchRoom = r.name.toLowerCase().includes(q);
    const units = r.units || [];
    const matchUnit = units.some(
      u =>
        u.name.toLowerCase().includes(q) ||
        (u.room_number || '').toLowerCase().includes(q)
    );
    return matchRoom || matchUnit;
  });
});

const getUnits = room => room.units || [];

const getActiveCount = room =>
  (room.units || []).filter(u => u.status === 'active').length;

const toggleExpand = roomId => {
  expandedRoomId.value = expandedRoomId.value === roomId ? null : roomId;
  editingUnitId.value = null;
  newUnitName.value = '';
  newUnitNumber.value = '';
};

// CRUD
const addUnit = async room => {
  if (!newUnitName.value.trim()) return;
  isSubmitting.value = true;
  try {
    const response = await MotorReservaRoomsAPI.createUnit(room.id, {
      name: newUnitName.value.trim(),
      room_number: newUnitNumber.value.trim(),
      room_type: newUnitType.value,
      status: 'active',
    });
    const idx = rooms.value.findIndex(r => r.id === room.id);
    if (idx >= 0) {
      const updatedUnits = [...(rooms.value[idx].units || []), response.data];
      rooms.value[idx] = { ...rooms.value[idx], units: updatedUnits };
    }
    newUnitName.value = '';
    newUnitNumber.value = '';
    useAlert(t('TDF.UNITS.ADD_SUCCESS'));
  } catch (error) {
    useAlert(t('TDF.UNITS.ADD_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const startEdit = unit => {
  editingUnitId.value = unit.id;
  editUnitName.value = unit.name;
  editUnitNumber.value = unit.room_number || '';
  editUnitType.value = unit.room_type || 'Unidade Habitacional';
};

const cancelEdit = () => {
  editingUnitId.value = null;
};

const saveEdit = async (room, unit) => {
  isSubmitting.value = true;
  try {
    await MotorReservaRoomsAPI.updateUnit(unit.id, {
      name: editUnitName.value.trim(),
      room_number: editUnitNumber.value.trim(),
      room_type: editUnitType.value,
    });
    const rIdx = rooms.value.findIndex(r => r.id === room.id);
    if (rIdx >= 0) {
      const units = [...(rooms.value[rIdx].units || [])];
      const uIdx = units.findIndex(u => u.id === unit.id);
      if (uIdx >= 0) {
        units[uIdx] = {
          ...units[uIdx],
          name: editUnitName.value.trim(),
          room_number: editUnitNumber.value.trim(),
          room_type: editUnitType.value,
        };
        rooms.value[rIdx] = { ...rooms.value[rIdx], units };
      }
    }
    editingUnitId.value = null;
    useAlert(t('TDF.UNITS.EDIT_SUCCESS'));
  } catch (error) {
    useAlert(t('TDF.UNITS.EDIT_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const toggleStatus = async (room, unit) => {
  const newStatus = unit.status === 'active' ? 'inactive' : 'active';
  try {
    await MotorReservaRoomsAPI.updateUnit(unit.id, { status: newStatus });
    const rIdx = rooms.value.findIndex(r => r.id === room.id);
    if (rIdx >= 0) {
      const units = [...(rooms.value[rIdx].units || [])];
      const uIdx = units.findIndex(u => u.id === unit.id);
      if (uIdx >= 0) {
        units[uIdx] = { ...units[uIdx], status: newStatus };
        rooms.value[rIdx] = { ...rooms.value[rIdx], units };
      }
    }
    useAlert(t('TDF.UNITS.STATUS_SUCCESS'));
  } catch (error) {
    useAlert(t('TDF.UNITS.STATUS_ERROR'));
  }
};

const deleteUnit = async (room, unit) => {
  try {
    await MotorReservaRoomsAPI.deleteUnit(unit.id);
    const rIdx = rooms.value.findIndex(r => r.id === room.id);
    if (rIdx >= 0) {
      const units = (rooms.value[rIdx].units || []).filter(
        u => u.id !== unit.id
      );
      rooms.value[rIdx] = { ...rooms.value[rIdx], units };
    }
    useAlert(t('TDF.UNITS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('TDF.UNITS.DELETE_ERROR'));
  }
};

const fetchRooms = async () => {
  isLoading.value = true;
  try {
    const response = await MotorReservaRoomsAPI.get();
    rooms.value = response.data;
  } catch (error) {
    useAlert(t('TDF.COMMON.LOADING'));
  } finally {
    isLoading.value = false;
  }
};

onBeforeMount(fetchRooms);
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div>
      <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
        {{ t('TDF.UNITS.HEADER') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ t('TDF.UNITS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Busca -->
    <input
      v-model="searchQuery"
      type="text"
      :class="ic"
      :placeholder="t('TDF.UNITS.SEARCH_PLACEHOLDER')"
    />

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="text-slate-500">{{ t('TDF.COMMON.LOADING') }}</span>
    </div>

    <!-- Lista de Quartos com Unidades -->
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="room in filteredRooms"
        :key="room.id"
        class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
      >
        <!-- Header do Quarto -->
        <button
          class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
          @click="toggleExpand(room.id)"
        >
          <div class="flex items-center gap-3 min-w-0">
            <span
              class="w-5 h-5 transition-transform duration-200 i-lucide-chevron-right text-slate-400"
              :class="{ 'rotate-90': expandedRoomId === room.id }"
            />
            <span
              v-if="room.room_type"
              class="shrink-0 text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            >
              {{ room.room_type }}
            </span>
            <span
              class="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate"
            >
              {{ room.name }}
            </span>
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <span
              class="text-xs font-medium px-2 py-1 rounded-full"
              :class="
                getActiveCount(room) > 0
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
              "
            >
              {{ getActiveCount(room) }}/{{ getUnits(room).length }}
              {{ t('TDF.UNITS.UNITS_LABEL') }}
            </span>
          </div>
        </button>

        <!-- Conteúdo expandido -->
        <div
          v-if="expandedRoomId === room.id"
          class="border-t border-slate-100 dark:border-slate-700"
        >
          <!-- Formulário inline para nova unidade -->
          <div
            class="flex gap-2 items-end px-4 py-3 bg-slate-50/50 dark:bg-slate-900/20"
          >
            <div class="flex-1">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1"
              >
                {{ t('TDF.UNITS.COL_NAME') }}
              </label>
              <input
                v-model="newUnitName"
                type="text"
                :class="ic"
                :placeholder="t('TDF.UNITS.NAME_PLACEHOLDER')"
                @keyup.enter="addUnit(room)"
              />
            </div>
            <div class="w-24">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1"
              >
                {{ t('TDF.UNITS.COL_NUMBER') }}
              </label>
              <input
                v-model="newUnitNumber"
                type="text"
                :class="ic"
                :placeholder="t('TDF.UNITS.NUMBER_PLACEHOLDER')"
                @keyup.enter="addUnit(room)"
              />
            </div>
            <div class="w-44">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1"
              >
                {{ t('TDF.UNITS.COL_TYPE') }}
              </label>
              <select v-model="newUnitType" :class="ic">
                <option v-for="rt in roomTypeOptions" :key="rt" :value="rt">
                  {{ rt }}
                </option>
              </select>
            </div>
            <Button
              :label="t('TDF.UNITS.ADD_BUTTON')"
              size="sm"
              :disabled="!newUnitName.trim() || isSubmitting"
              @click="addUnit(room)"
            />
          </div>

          <!-- Tabela de unidades -->
          <table v-if="getUnits(room).length" class="w-full text-sm">
            <thead>
              <tr
                class="bg-slate-50 dark:bg-slate-900/30 text-left border-t border-slate-100 dark:border-slate-700"
              >
                <th
                  class="px-4 py-2 font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('TDF.UNITS.COL_TYPE') }}
                </th>
                <th
                  class="px-3 py-2 font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('TDF.UNITS.COL_NAME') }}
                </th>
                <th
                  class="px-3 py-2 font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('TDF.UNITS.COL_NUMBER') }}
                </th>
                <th
                  class="px-3 py-2 font-medium text-slate-600 dark:text-slate-300 text-center"
                >
                  {{ t('TDF.UNITS.COL_STATUS') }}
                </th>
                <th
                  class="px-3 py-2 font-medium text-slate-600 dark:text-slate-300 text-right"
                >
                  {{ t('TDF.UNITS.COL_ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
              <tr
                v-for="unit in getUnits(room)"
                :key="unit.id"
                class="hover:bg-slate-50/50 dark:hover:bg-slate-800/50"
              >
                <!-- Modo edição -->
                <template v-if="editingUnitId === unit.id">
                  <td class="px-4 py-2">
                    <select
                      v-model="editUnitType"
                      :class="ic + ' !py-1 text-xs'"
                    >
                      <option
                        v-for="rt in roomTypeOptions"
                        :key="rt"
                        :value="rt"
                      >
                        {{ rt }}
                      </option>
                    </select>
                  </td>
                  <td class="px-3 py-2">
                    <input
                      v-model="editUnitName"
                      type="text"
                      :class="ic + ' !py-1 text-xs'"
                    />
                  </td>
                  <td class="px-3 py-2">
                    <input
                      v-model="editUnitNumber"
                      type="text"
                      :class="ic + ' !py-1 text-xs'"
                    />
                  </td>
                  <td class="px-3 py-2 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full"
                      :class="
                        unit.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                      "
                    >
                      {{ unit.status === 'active' ? 'A' : 'I' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-right">
                    <div class="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        class="text-xs text-woot-500 hover:text-woot-700 font-medium"
                        @click="saveEdit(room, unit)"
                      >
                        {{ t('TDF.COMMON.SAVE') }}
                      </button>
                      <button
                        type="button"
                        class="text-xs text-slate-500 hover:text-slate-700"
                        @click="cancelEdit"
                      >
                        {{ t('TDF.COMMON.CANCEL') }}
                      </button>
                    </div>
                  </td>
                </template>

                <!-- Modo visualização -->
                <template v-else>
                  <td class="px-4 py-2">
                    <span
                      v-if="unit.room_type"
                      class="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                    >
                      {{ unit.room_type }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-slate-800 dark:text-slate-100">
                    {{ unit.name }}
                  </td>
                  <td class="px-3 py-2 text-slate-500">
                    {{ unit.room_number || '-' }}
                  </td>
                  <td class="px-3 py-2 text-center">
                    <button
                      type="button"
                      class="text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors"
                      :class="
                        unit.status === 'active'
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-400'
                      "
                      :title="t('TDF.UNITS.TOGGLE_STATUS')"
                      @click="toggleStatus(room, unit)"
                    >
                      {{ unit.status === 'active' ? 'A' : 'I' }}
                    </button>
                  </td>
                  <td class="px-3 py-2 text-right">
                    <div class="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        class="text-xs text-woot-500 hover:text-woot-700"
                        @click="startEdit(unit)"
                      >
                        {{ t('TDF.COMMON.EDIT') }}
                      </button>
                      <button
                        type="button"
                        class="text-xs text-red-500 hover:text-red-700"
                        @click="deleteUnit(room, unit)"
                      >
                        {{ t('TDF.COMMON.DELETE') }}
                      </button>
                    </div>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>

          <!-- Mensagem vazia -->
          <p v-else class="text-xs text-slate-400 text-center py-4">
            {{ t('TDF.UNITS.EMPTY') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Mensagem sem quartos -->
    <p
      v-if="!isLoading && !filteredRooms.length"
      class="text-sm text-slate-500 text-center py-8"
    >
      {{ t('TDF.UNITS.NO_ROOMS') }}
    </p>
  </div>
</template>
